import type {ItemRefinementObject, ItemRefinementStrokeObject} from '@PosterWhiteboard/classes/item-refinement.types';
import {CutoutRefineTool} from '@PosterWhiteboard/classes/item-refinement.types';

/**
 * An image refinement is a list of erase/restore brush strokes stored in the poster itself. Points and
 * widths sit on an integer grid whose units are the same along both axes: the image's long side spans
 * {@link REFINEMENT_GRID_SIZE}. The strokes replay at whatever size the image loads at, onto two alpha
 * masks: erase marks pixels removed from the cutout, restore marks pixels painted back from the original.
 * A stroke in one mode clears the other mode's mask under it, so a pixel is never both.
 */
export const REFINEMENT_GRID_SIZE = 4096;
/** Twin of ImageRefinementVO::MAX_POINTS. Past it a refinement no longer fits the poster and is flattened into a new image. */
export const REFINEMENT_MAX_POINTS = 10000;
/** A brush centred just off the picture still paints its edge, so stored points may sit this far outside the grid. */
export const REFINEMENT_GRID_MARGIN = REFINEMENT_GRID_SIZE;
/** The live masks a refine session paints on are capped at this size, so painting stays fast on very large images. */
export const BACKGROUND_REFINEMENT_MASK_MAX_DIMENSION = 2048;
/** Douglas-Peucker tolerance, in mask pixels, for dropping points that do not change a stroke's shape. */
const STROKE_SIMPLIFY_TOLERANCE_PX = 0.5;
const COORDINATES_PER_POINT = 2;

export interface RefinementMasks {
  /** Alpha marks pixels removed from the cutout */
  eraseMask: HTMLCanvasElement;
  /** Alpha marks pixels painted back from the original image */
  restoreMask: HTMLCanvasElement;
}

export interface RefinementMaskSize {
  width: number;
  height: number;
}

export interface RefinedCutoutSources extends RefinementMasks {
  cutout: CanvasImageSource;
  /** The pre-removal image restore strokes paint from; null skips the restore pass, for strokes that only erase or an original that could not be loaded. */
  original: CanvasImageSource | null;
  width: number;
  height: number;
}

export interface RefinementPoint {
  x: number;
  y: number;
}

export interface RefinementSegment {
  tool: CutoutRefineTool;
  from: RefinementPoint;
  to: RefinementPoint;
  lineWidth: number;
}

export interface RefinementStrokeInput {
  tool: CutoutRefineTool;
  /** Brush diameter in mask pixels */
  lineWidth: number;
  /** The brush path in mask pixels */
  points: RefinementPoint[];
  maskSize: RefinementMaskSize;
}

/**
 * Replays a refinement onto fresh erase and restore masks of the given size.
 */
export const rasterizeRefinement = (refinement: ItemRefinementObject | null, width: number, height: number): RefinementMasks => {
  const masks = createEmptyRefinementMasks(width, height);
  if (refinement === null) {
    return masks;
  }
  const pixelsPerGridUnit = 1 / getGridUnitsPerPixel({width, height});
  for (const stroke of refinement.strokes) {
    paintStoredStroke(masks, stroke, pixelsPerGridUnit);
  }
  return masks;
};

/**
 * Paints one brush segment: onto the tool's own mask, and off the other one. A segment whose ends meet is a dot.
 */
export const paintRefinementSegment = (masks: RefinementMasks, {tool, from, to, lineWidth}: RefinementSegment): void => {
  const paintedMask = tool === CutoutRefineTool.ERASE ? masks.eraseMask : masks.restoreMask;
  const clearedMask = tool === CutoutRefineTool.ERASE ? masks.restoreMask : masks.eraseMask;
  strokeSegment(getContext(paintedMask), {from, to, lineWidth, operation: 'source-over'});
  strokeSegment(getContext(clearedMask), {from, to, lineWidth, operation: 'destination-out'});
};

/**
 * Turns a painted brush path into its stored form: simplified, then moved onto the integer grid. The width
 * is capped at the grid's span, the same cap ImageRefinementVO applies, so a stored stroke is never dropped.
 */
export const createRefinementStroke = ({tool, lineWidth, points, maskSize}: RefinementStrokeInput): ItemRefinementStrokeObject => {
  const gridUnitsPerPixel = getGridUnitsPerPixel(maskSize);
  return {
    tool,
    width: Math.min(REFINEMENT_GRID_SIZE, Math.max(1, Math.round(lineWidth * gridUnitsPerPixel))),
    points: toGridCoordinates(simplifyPath(points, STROKE_SIMPLIFY_TOLERANCE_PX), gridUnitsPerPixel),
  };
};

export const countRefinementPoints = (strokes: ItemRefinementStrokeObject[]): number => {
  return strokes.reduce((total, stroke) => {
    return total + stroke.points.length / COORDINATES_PER_POINT;
  }, 0);
};

/** Only restore strokes paint from the original image, so an erase-only refinement never needs it. */
export const hasRestoreStrokes = (strokes: ItemRefinementStrokeObject[]): boolean => {
  return strokes.some((stroke) => {
    return stroke.tool === CutoutRefineTool.RESTORE;
  });
};

/**
 * Composites (cutout − erase) + (original ∩ restore) at the given size, onto a new canvas. Masks are scaled to fit.
 */
export const compositeRefinedCutout = (sources: RefinedCutoutSources): HTMLCanvasElement => {
  return drawRefinedCutout(createCanvas(sources.width, sources.height), sources);
};

/**
 * The same composite onto one canvas reused across calls, for a preview that is drawn on straight away: the
 * result is only valid until the next call. Saves a full-size allocation on every brush move.
 */
export const compositeRefinedCutoutForPreview = (sources: RefinedCutoutSources): HTMLCanvasElement => {
  return drawRefinedCutout(getScratchCanvas(previewResultScratch, sources.width, sources.height), sources);
};

/** Both passes start by clearing what an earlier call left on their canvas. */
const drawRefinedCutout = (result: HTMLCanvasElement, {cutout, original, eraseMask, restoreMask, width, height}: RefinedCutoutSources): HTMLCanvasElement => {
  const resultContext = getContext(result);
  resultContext.globalCompositeOperation = 'source-over';
  resultContext.clearRect(0, 0, width, height);
  resultContext.drawImage(cutout, 0, 0, width, height);
  resultContext.globalCompositeOperation = 'destination-out';
  resultContext.drawImage(eraseMask, 0, 0, width, height);
  if (original === null) {
    return result;
  }

  const restoredPixels = getScratchCanvas(restoreScratch, width, height);
  const restoredContext = getContext(restoredPixels);
  restoredContext.globalCompositeOperation = 'source-over';
  restoredContext.clearRect(0, 0, width, height);
  restoredContext.drawImage(original, 0, 0, width, height);
  restoredContext.globalCompositeOperation = 'destination-in';
  restoredContext.drawImage(restoreMask, 0, 0, width, height);

  resultContext.globalCompositeOperation = 'source-over';
  resultContext.drawImage(restoredPixels, 0, 0);
  return result;
};

interface ScratchCanvasSlot {
  canvas: HTMLCanvasElement | null;
}

const previewResultScratch: ScratchCanvasSlot = {canvas: null};
const restoreScratch: ScratchCanvasSlot = {canvas: null};

/**
 * A canvas kept across calls while it fits the live-mask cap. A bigger one-off composite (a flatten, a high-res
 * load) gets a throwaway canvas instead, so that much memory is never pinned between calls.
 */
const getScratchCanvas = (slot: ScratchCanvasSlot, width: number, height: number): HTMLCanvasElement => {
  if (width * height > BACKGROUND_REFINEMENT_MASK_MAX_DIMENSION ** 2) {
    return createCanvas(width, height);
  }
  if (slot.canvas?.width !== width || slot.canvas.height !== height) {
    slot.canvas = createCanvas(width, height);
  }
  return slot.canvas;
};

/**
 * Size for masks over a source image: the source size, capped so the long side fits the limit, which is the
 * live-session cap unless the caller passes its own. Neither side rounds down to nothing: a canvas with a
 * zero side cannot be drawn from.
 */
export const getRefinementMaskSize = (sourceWidth: number, sourceHeight: number, maxDimension = BACKGROUND_REFINEMENT_MASK_MAX_DIMENSION): RefinementMaskSize => {
  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
  const toMaskSide = (sourceSide: number): number => {
    return Math.max(1, Math.round(sourceSide * scale));
  };
  return {width: toMaskSide(sourceWidth), height: toMaskSide(sourceHeight)};
};

export const createEmptyRefinementMasks = (width: number, height: number): RefinementMasks => {
  return {eraseMask: createCanvas(width, height), restoreMask: createCanvas(width, height)};
};

/**
 * Ramer-Douglas-Peucker: keeps the end points and every point further than the tolerance from the line
 * through its neighbours that survive.
 */
export const simplifyPath = (points: RefinementPoint[], tolerance: number): RefinementPoint[] => {
  if (points.length <= 2) {
    return points;
  }
  const isKept = new Array<boolean>(points.length).fill(false);
  isKept[0] = true;
  isKept[points.length - 1] = true;
  const pendingRanges: [number, number][] = [[0, points.length - 1]];

  for (let range = pendingRanges.pop(); range !== undefined; range = pendingRanges.pop()) {
    const [startIndex, endIndex] = range;
    const {index: farthestIndex, distance} = findFarthestPoint(points, startIndex, endIndex);
    if (farthestIndex !== -1 && distance > tolerance) {
      isKept[farthestIndex] = true;
      pendingRanges.push([startIndex, farthestIndex], [farthestIndex, endIndex]);
    }
  }

  return points.filter((_point, index) => {
    return isKept[index];
  });
};

const getGridUnitsPerPixel = ({width, height}: RefinementMaskSize): number => {
  return REFINEMENT_GRID_SIZE / Math.max(width, height);
};

const paintStoredStroke = (masks: RefinementMasks, stroke: ItemRefinementStrokeObject, pixelsPerGridUnit: number): void => {
  const lineWidth = stroke.width * pixelsPerGridUnit;
  let previousPoint: RefinementPoint | null = null;
  for (let offset = 0; offset + 1 < stroke.points.length; offset += COORDINATES_PER_POINT) {
    const point = {x: stroke.points[offset] * pixelsPerGridUnit, y: stroke.points[offset + 1] * pixelsPerGridUnit};
    paintRefinementSegment(masks, {tool: stroke.tool, from: previousPoint ?? point, to: point, lineWidth});
    previousPoint = point;
  }
};

/** Integer grid coordinates as one flat x0,y0,x1,y1 list, without consecutive repeats. */
const toGridCoordinates = (points: RefinementPoint[], gridUnitsPerPixel: number): number[] => {
  const coordinates: number[] = [];
  for (const point of points) {
    const x = toGridUnit(point.x * gridUnitsPerPixel);
    const y = toGridUnit(point.y * gridUnitsPerPixel);
    const isRepeat = coordinates.length > 0 && coordinates[coordinates.length - 2] === x && coordinates[coordinates.length - 1] === y;
    if (!isRepeat) {
      coordinates.push(x, y);
    }
  }
  return coordinates;
};

const toGridUnit = (value: number): number => {
  return Math.min(REFINEMENT_GRID_SIZE + REFINEMENT_GRID_MARGIN, Math.max(-REFINEMENT_GRID_MARGIN, Math.round(value)));
};

interface FarthestPoint {
  index: number;
  distance: number;
}

const findFarthestPoint = (points: RefinementPoint[], startIndex: number, endIndex: number): FarthestPoint => {
  let farthest: FarthestPoint = {index: -1, distance: 0};
  for (let index = startIndex + 1; index < endIndex; index++) {
    const distance = getDistanceFromLine(points[index], points[startIndex], points[endIndex]);
    if (distance > farthest.distance) {
      farthest = {index, distance};
    }
  }
  return farthest;
};

const getDistanceFromLine = (point: RefinementPoint, lineStart: RefinementPoint, lineEnd: RefinementPoint): number => {
  const lineX = lineEnd.x - lineStart.x;
  const lineY = lineEnd.y - lineStart.y;
  const lineLength = Math.hypot(lineX, lineY);
  if (lineLength === 0) {
    return Math.hypot(point.x - lineStart.x, point.y - lineStart.y);
  }
  return Math.abs(lineX * (lineStart.y - point.y) - (lineStart.x - point.x) * lineY) / lineLength;
};

interface ContextSegment {
  from: RefinementPoint;
  to: RefinementPoint;
  lineWidth: number;
  operation: GlobalCompositeOperation;
}

const strokeSegment = (context: CanvasRenderingContext2D, {from, to, lineWidth, operation}: ContextSegment): void => {
  context.globalCompositeOperation = operation;
  context.fillStyle = '#000';
  context.strokeStyle = '#000';
  context.lineWidth = lineWidth;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.beginPath();
  if (from.x === to.x && from.y === to.y) {
    context.arc(to.x, to.y, lineWidth / 2, 0, Math.PI * 2);
    context.fill();
  } else {
    context.moveTo(from.x, from.y);
    context.lineTo(to.x, to.y);
    context.stroke();
  }
  context.globalCompositeOperation = 'source-over';
};

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

/** Nothing here reads pixels back, so the canvases stay GPU-backed for the per-frame preview blit. */
const getContext = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to initialize canvas context');
  }
  return context;
};
